import { useState, useEffect } from "react";
import { RefreshCw, Check, Loader2 } from "lucide-react";
import { Select } from "../ui";
import { zendeskApi } from "../../api/zendesk";

interface Brand {
  zendesk_brand_id: string;
  name: string;
  subdomain: string;
  is_active: boolean;
  datasource_key: string | null;
}

interface DatasourceOption {
  key: string;
  name: string;
}

type SaveState = "idle" | "saving" | "saved" | "error";

export default function ZendeskSettings() {
  const [configured, setConfigured] = useState<boolean | null>(null);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [datasources, setDatasources] = useState<DatasourceOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saveStates, setSaveStates] = useState<Record<string, SaveState>>({});
  const [refreshing, setRefreshing] = useState(false);

  async function loadAll() {
    setLoading(true);
    setError("");
    try {
      const statusRes = await zendeskApi.getStatus();
      const isConfigured: boolean = statusRes.data.configured;
      setConfigured(isConfigured);
      if (isConfigured) {
        const [brandsRes, dsRes] = await Promise.all([
          zendeskApi.listBrands(),
          zendeskApi.listDatasources(),
        ]);
        setBrands(brandsRes.data);
        setDatasources(dsRes.data);
      }
    } catch {
      setError("Failed to load Zendesk settings.");
    } finally {
      setLoading(false);
    }
  }

  async function refreshBrands() {
    setRefreshing(true);
    try {
      const res = await zendeskApi.listBrands();
      setBrands(res.data);
    } catch {
      setError("Failed to refresh brands.");
    } finally {
      setRefreshing(false);
    }
  }

  async function handleDatasourceChange(brand: Brand, newKey: string) {
    const datasourceKey = newKey === "" ? null : newKey;
    setSaveStates((s) => ({ ...s, [brand.zendesk_brand_id]: "saving" }));
    try {
      await zendeskApi.updateBrandMapping(brand.zendesk_brand_id, datasourceKey, brand.name);
      setBrands((prev) =>
        prev.map((b) =>
          b.zendesk_brand_id === brand.zendesk_brand_id ? { ...b, datasource_key: datasourceKey } : b
        )
      );
      setSaveStates((s) => ({ ...s, [brand.zendesk_brand_id]: "saved" }));
      setTimeout(() => {
        setSaveStates((s) => ({ ...s, [brand.zendesk_brand_id]: "idle" }));
      }, 2000);
    } catch {
      setSaveStates((s) => ({ ...s, [brand.zendesk_brand_id]: "error" }));
    }
  }

  useEffect(() => {
    loadAll();
  }, []);

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-6 w-48 animate-pulse rounded bg-gray-200" />
        <div className="rounded-xl border border-gray-200 overflow-hidden">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center gap-4 p-4 border-b border-gray-100 last:border-0">
              <div className="h-4 w-40 animate-pulse rounded bg-gray-200" />
              <div className="h-4 w-28 animate-pulse rounded bg-gray-200" />
              <div className="ml-auto h-8 w-40 animate-pulse rounded bg-gray-200" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (configured === false) {
    return (
      <div className="rounded-xl border border-gray-200 bg-gray-50 p-6">
        <h3 className="text-sm font-semibold text-gray-700 mb-2">Zendesk not configured</h3>
        <p className="text-sm text-gray-500">
          Set the following environment variables to enable the Zendesk integration:
        </p>
        <ul className="mt-3 space-y-1 text-sm font-mono text-gray-600">
          <li>ZENDESK_SUBDOMAIN</li>
          <li>ZENDESK_EMAIL</li>
          <li>ZENDESK_API_TOKEN</li>
        </ul>
      </div>
    );
  }

  const datasourceOptions = [
    { value: "", label: "No mapping" },
    ...datasources.map((ds) => ({ value: ds.key, label: ds.name })),
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-gray-900">Zendesk brand mappings</h2>
        <button
          type="button"
          onClick={refreshBrands}
          disabled={refreshing}
          className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      {error && (
        <p className="text-sm text-red-600">{error}</p>
      )}

      <div className="rounded-xl border border-gray-200 overflow-hidden bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50">
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-400">Brand</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-400">Subdomain</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-400">Datasource</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {brands.filter((b) => b.is_active).length === 0 ? (
              <tr>
                <td colSpan={3} className="px-4 py-6 text-center text-gray-400 text-sm">
                  No brands found in Zendesk.
                </td>
              </tr>
            ) : (
              brands.filter((b) => b.is_active).map((brand) => {
                const state = saveStates[brand.zendesk_brand_id] ?? "idle";
                return (
                  <tr key={brand.zendesk_brand_id} className="hover:bg-gray-50/50">
                    <td className="px-4 py-3 font-medium text-gray-800">
                      {brand.name}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-gray-500">{brand.subdomain}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Select
                          value={brand.datasource_key ?? ""}
                          onChange={(e) => handleDatasourceChange(brand, e.target.value)}
                          disabled={state === "saving"}
                          size="sm"
                          aria-label={`Datasource for ${brand.name}`}
                        >
                          {datasourceOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </Select>
                        {state === "saving" && (
                          <Loader2 size={14} className="animate-spin text-gray-400 shrink-0" />
                        )}
                        {state === "saved" && (
                          <Check size={14} className="text-green-500 shrink-0" />
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
